/**
 * V70G6B — 엔글 오목 Room 페이지.
 *   - URL: /tournament/game/omok/room/:roomId
 *   - onSnapshot 실시간 구독 (BracketDetailPage.tsx 패턴)
 *   - waiting → 대기실 (방 코드 표시, 복사 기능)
 *   - playing → 보드 + 플레이어 정보 + 턴 안내
 *   - finished → 결과 모달
 *   - abandoned → 안내 후 lobby 복귀
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, LogOut, Flag, ArrowLeft, Clock, Trophy, BookOpen } from 'lucide-react';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import { useOmokRoomSubscription, useOmokActions, saveLastRoomId } from './useOmokRoom';
import { clearLastRoomIdIf, isOmokRoomEnded } from './omokLastRoom';
import { OmokBoard } from './OmokBoard';
import { OmokRulesModal } from './OmokRulesModal';
import type { OmokPublicRoom, OmokSide } from './types';
import { buildResultView, formatTurnClock, remainingTurnSeconds, resolveMySide, toOmokErrorMessage, turnMessage } from './omokView';
import { OmokActiveGameConfirm } from './OmokActiveGameConfirm';

// V70G6E — 진영 공개 연출 지속 시간 (ms). reduced-motion 사용자는 짧게 (300ms 로).
const REVEAL_DURATION_FULL_MS = 3000;
const REVEAL_DURATION_REDUCED_MS = 300;

function tsToMs(ts: unknown): number {
  if (!ts) return 0;
  const t = ts as any;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  return 0;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    try { mq.addEventListener('change', handler); } catch { /* older browsers */ }
    return () => {
      try { mq.removeEventListener('change', handler); } catch { /* ignore */ }
    };
  }, []);
  return reduced;
}

export function OmokRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useCurrentUser();
  const { room, loading, error: subErr } = useOmokRoomSubscription(roomId || null);
  const { playMove, resignRoom, leaveRoom, getMyParticipation, claimTimeout, busy, error: actErr, clearError } = useOmokActions();
  // V70G8D — 서버 시각 보정값 (serverNow ≈ Date.now() + offset). PC 시계와 무관하게 서버 마감 기준 표시.
  const clockOffsetRef = useRef<number>(0);
  const [copied, setCopied] = useState(false);
  // 플레이어 식별: 서버가 UID 로 확인한 내 publicPlayerId 를 실시간 방 문서의 흑/백과 비교한다.
  //   방장은 waiting 중 임시로 흑 자리에 있다가 참가자 입장 시 서버 랜덤으로 백이 될 수 있으므로
  //   입장 시점의 side 를 고정하지 않는다. 착수 권한은 서버 Callable 이 UID 로 재검증.
  const [myPublicPlayerId, setMyPublicPlayerId] = useState<string | null>(null);
  const [sideLoading, setSideLoading] = useState<boolean>(true);
  const [sideError, setSideError] = useState<string | null>(null);
  // V70G8E — 다른 방에서 진행 중인 내 대국 (서버 확인). 이 방 플레이어가 아니면 기권 확인 전까지 진입 보류.
  const [participantSide, setParticipantSide] = useState<OmokSide | null>(null);
  const [otherActiveRoomIds, setOtherActiveRoomIds] = useState<string[]>([]);
  const [guardBusy, setGuardBusy] = useState<boolean>(false);
  const [guardError, setGuardError] = useState<string | null>(null);
  const roomStatus = room?.status;

  // V70G8J — 승패 확정(또는 폐기) 시점에 복귀 참조 정리. [게임 종료] 버튼·로비 버튼·뒤로가기 모두 동일.
  //   승패 기록은 서버 문서 그대로이며 여기서는 클라이언트 참조만 지운다.
  useEffect(() => {
    if (roomId && isOmokRoomEnded(roomStatus)) clearLastRoomIdIf(roomId);
  }, [roomId, roomStatus]);

  useEffect(() => {
    if (!roomId || !user) return;
    let cancelled = false;
    setSideLoading(true);
    setSideError(null);
    (async () => {
      try {
        const t0 = Date.now();
        const r = await getMyParticipation(roomId);
        const t1 = Date.now();
        if (cancelled) return;
        if (typeof r.serverNowMs === 'number') {
          clockOffsetRef.current = r.serverNowMs - Math.round((t0 + t1) / 2);
        }
        setMyPublicPlayerId(r.publicPlayerId);
        setParticipantSide(r.side);
        setOtherActiveRoomIds(Array.isArray(r.activeRoomIds) ? r.activeRoomIds : []);
      } catch (e: any) {
        if (cancelled) return;
        setSideError(e?.message || String(e));
      } finally {
        if (!cancelled) setSideLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // 계정 변경 또는 방 상태 전환(waiting→playing 등) 시 재확인.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, user?.profile.id, roomStatus]);

  const mySide: OmokSide | null = resolveMySide(room, myPublicPlayerId);

  const reducedMotion = usePrefersReducedMotion();
  const revealDurationMs = reducedMotion ? REVEAL_DURATION_REDUCED_MS : REVEAL_DURATION_FULL_MS;

  // V70G6E — 진영 공개 연출 phase 판단.
  //   서버가 waiting→playing 전환 시 playingStartedAt 기록. FE 는 (now - playingStartedAt) < revealDurationMs 때만 reveal phase.
  //   재접속 시: 시간이 지났으면 skip. 서버 턴 검증과 모순 없음 (reveal 중엔 FE 가 보드 interaction 비활성).
  //   room 이 null 일 수 있으므로 Optional chaining 으로 안전 처리 — React Hooks 는 early return 전에 호출해야 함.
  const playingStartedMs = useMemo(() => tsToMs(room?.playingStartedAt), [room?.playingStartedAt]);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  useEffect(() => {
    if (room?.status !== 'playing' || !playingStartedMs) return;
    const elapsed = Date.now() - playingStartedMs;
    if (elapsed >= revealDurationMs) return; // 이미 지남 — timer 불필요
    const t = setTimeout(() => setNowMs(Date.now()), revealDurationMs - elapsed + 50);
    return () => clearTimeout(t);
  }, [room?.status, playingStartedMs, revealDurationMs]);
  // 규칙 모달 — 대국 중에도 열람 가능 (사용자 verbatim)
  const [rulesOpen, setRulesOpen] = useState<boolean>(false);

  // V70G8D — 한 수 30초 카운트다운 (표시 전용, 판정은 서버). 새로고침해도 서버 마감 시각으로 복구.
  const turnDeadlineMs = useMemo(() => {
    const ms = tsToMs(room?.turnDeadlineAt);
    return ms > 0 ? ms : null;
  }, [room?.turnDeadlineAt]);
  const timerActive = room?.status === 'playing' && turnDeadlineMs !== null && !!room?.turnTimeLimitMs;
  const [clockTick, setClockTick] = useState<number>(Date.now());
  useEffect(() => {
    if (!timerActive) return undefined;
    setClockTick(Date.now());
    const t = setInterval(() => setClockTick(Date.now()), 250);
    return () => clearInterval(t);
  }, [timerActive, turnDeadlineMs]);
  const serverNowMs = clockTick + clockOffsetRef.current;
  const turnSecondsLeft = timerActive ? remainingTurnSeconds(turnDeadlineMs, serverNowMs) : null;
  const localTimeUp = timerActive && turnDeadlineMs !== null && serverNowMs > turnDeadlineMs;

  // 0초가 지나면 서버에 확정 요청 (마감당 1회, 서버가 아직이라고 하면 보정 후 재시도)
  const claimedDeadlineRef = useRef<number | null>(null);
  const [claimRetry, setClaimRetry] = useState<number>(0);
  useEffect(() => {
    if (!roomId || !localTimeUp || turnDeadlineMs === null) return;
    if (claimedDeadlineRef.current === turnDeadlineMs) return;
    claimedDeadlineRef.current = turnDeadlineMs;
    const retryAfter = (ms: number) => window.setTimeout(() => {
      claimedDeadlineRef.current = null;
      setClaimRetry((n) => n + 1);
    }, ms);
    claimTimeout(roomId)
      .then((r) => {
        if (!r.finalized && typeof r.serverNowMs === 'number') {
          clockOffsetRef.current = r.serverNowMs - Date.now();
          retryAfter(1_000);
        }
      })
      .catch(() => { retryAfter(3_000); });
  }, [roomId, localTimeUp, turnDeadlineMs, claimTimeout, claimRetry]);

  if (!roomId) return <div style={{ padding: 24 }}>잘못된 접근입니다.</div>;
  if (loading || userLoading) return <div style={{ padding: 24, color: '#64748b' }}>방 정보를 불러오는 중…</div>;
  if (subErr) return <div style={{ padding: 24, color: '#991b1b' }}>{subErr}</div>;
  if (!room) return <div style={{ padding: 24, color: '#991b1b' }}>방을 찾을 수 없습니다.</div>;

  // V70G8E — URL 직접 진입 등으로 다른 방에 들어왔는데 진행 중인 내 대국이 있으면 기권 확인 전까지 보류
  const entryGuarded = !sideLoading && participantSide === null && otherActiveRoomIds.length > 0;
  if (entryGuarded) {
    const handleGuardCancel = () => {
      // [취소] → 진행 중 대국 유지, 이 방 진입 안 함
      navigate(`/tournament/game/omok/room/${otherActiveRoomIds[0]}`);
    };
    const handleGuardConfirm = async () => {
      setGuardBusy(true);
      setGuardError(null);
      let lastErr: string | null = null;
      // 기존 기권 경로 그대로 사용 (시간 초과가 먼저 확정됐으면 서버가 시간 초과 결과 유지)
      for (const id of otherActiveRoomIds) {
        try { await resignRoom(id); } catch (e) { lastErr = toOmokErrorMessage(e); }
      }
      clearError();
      try {
        // 서버에서 실제로 정리됐는지 다시 확인한 뒤에만 이 방 진입
        const r = await getMyParticipation(roomId);
        setParticipantSide(r.side);
        setMyPublicPlayerId(r.publicPlayerId);
        const remaining = Array.isArray(r.activeRoomIds) ? r.activeRoomIds : [];
        setOtherActiveRoomIds(remaining);
        if (remaining.length > 0) setGuardError(lastErr || '진행 중인 대국 기권에 실패했습니다. 다시 시도해 주세요.');
      } catch (e) {
        setGuardError(toOmokErrorMessage(e));
      } finally {
        setGuardBusy(false);
      }
    };
    return (
      <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
        <OmokActiveGameConfirm
          busy={guardBusy || busy}
          error={guardError}
          targetLabel={room.status === 'waiting' ? '이 대기방' : '이 방'}
          onCancel={handleGuardCancel}
          onConfirm={() => { void handleGuardConfirm(); }}
        />
      </div>
    );
  }

  const isRevealPhase = room.status === 'playing' && playingStartedMs > 0 && (nowMs - playingStartedMs) < revealDurationMs;
  const isMyTurn = mySide !== null && room.nextTurn === mySide && room.status === 'playing' && !isRevealPhase && !sideLoading && !localTimeUp;
  const amIParticipant = mySide !== null;
  const amIParticipantUnknown = sideLoading; // side 조회 중엔 보드 interaction 금지

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(room.roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handlePlay = async (x: number, y: number) => {
    try { await playMove(roomId, x, y); } catch { /* error state */ }
  };
  const handleResign = async () => {
    if (!window.confirm('기권하시겠습니까? 상대 승리로 처리됩니다.')) return;
    try { await resignRoom(roomId); } catch { /* ignore */ }
  };
  const handleLeave = async () => {
    try { await leaveRoom(roomId); } catch { /* ignore */ }
    saveLastRoomId(null);
    navigate('/tournament/game/omok');
  };
  const handleExit = () => {
    saveLastRoomId(null);
    navigate('/tournament/game/omok');
  };

  // 결과 모달 분기
  const isFinished = room.status === 'finished' || room.status === 'abandoned';

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <button
            style={backBtnStyle}
            onClick={() => navigate('/tournament/game/omok')}
          ><ArrowLeft size={16} /> 로비</button>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: '#0f172a' }}>
            엔글 <span style={{ color: '#065f46' }}>오목</span>
          </h1>
          {room.status === 'waiting' && amIParticipant && (
            <button style={dangerBtnStyle} onClick={handleLeave} disabled={busy}>
              <LogOut size={14} /> 방 삭제
            </button>
          )}
          {room.status === 'playing' && amIParticipant && (
            <button style={dangerBtnStyle} onClick={handleResign} disabled={busy}>
              <Flag size={14} /> 기권
            </button>
          )}
          {!amIParticipant && room.status !== 'finished' && (
            <button style={backBtnStyle} onClick={handleExit}>관전 종료</button>
          )}
          {/* V70G6E — 대국 중에도 규칙 열람 가능 */}
          <button
            style={{ ...backBtnStyle, background: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe' }}
            onClick={() => setRulesOpen(true)}
            data-testid="omok-rules-open-ingame"
          ><BookOpen size={14} /> 경기 규칙</button>
        </div>

        {(actErr || sideError) && (
          <div style={errBoxStyle}>
            <span>{actErr || sideError}</span>
            <button onClick={clearError} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#991b1b', fontSize: 18 }}>×</button>
          </div>
        )}

        {/* 상태별 UI */}
        {room.status === 'waiting' && (
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <Clock size={32} color="#065f46" style={{ marginBottom: 10 }} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>상대방 입장 대기 중…</h2>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>
              아래 방 코드를 상대방에게 알려주세요.
            </p>
            <div style={{ marginTop: 20, padding: '20px 40px', background: '#f1f5f9', borderRadius: 10, display: 'inline-block' }}>
              <div style={{ fontSize: 36, fontFamily: 'monospace', letterSpacing: 8, color: '#065f46', fontWeight: 700 }}>
                {room.roomCode}
              </div>
              <button
                style={{
                  marginTop: 10, padding: '8px 14px', background: copied ? '#10b981' : '#065f46', color: '#fff',
                  border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
                onClick={copyCode}
              >
                <Copy size={14} /> {copied ? '복사됨' : '코드 복사'}
              </button>
            </div>
            <div style={{ marginTop: 24, color: '#64748b', fontSize: 12 }}>
              방장: <strong>{room.playerBlack?.displayName || '-'}</strong>
              {room.playerBlack?.orgUnitName && ` (${room.playerBlack.orgUnitName})`}
              {' '}· 흑백은 참가자 입장 시 서버가 랜덤 결정합니다.
            </div>
          </div>
        )}

        {(room.status === 'playing' || isFinished) && (
          <>
            {/* V70G6E — 진영 공개 연출 phase (3초, reduced-motion 300ms) */}
            {isRevealPhase && !isFinished && (
              <RevealOverlay
                playerBlack={room.playerBlack}
                playerWhite={room.playerWhite}
                mySide={mySide}
                reducedMotion={reducedMotion}
              />
            )}

            {/* 플레이어 정보 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <PlayerCard
                side="black"
                player={room.playerBlack}
                isCurrentTurn={room.status === 'playing' && room.nextTurn === 'black'}
                isMe={mySide === 'black'}
              />
              <PlayerCard
                side="white"
                player={room.playerWhite}
                isCurrentTurn={room.status === 'playing' && room.nextTurn === 'white'}
                isMe={mySide === 'white'}
              />
            </div>

            {/* 상태 안내 */}
            {room.status === 'playing' && (
              <div style={{ textAlign: 'center', marginBottom: 12, color: '#065f46', fontSize: 14, fontWeight: 600 }}>
                {sideLoading
                  ? `확인 중… · ${room.moveCount}수`
                  : localTimeUp
                    ? `⏱ 시간 초과 — 결과 확인 중… · ${room.moveCount}수`
                    : turnMessage(amIParticipant, isMyTurn, room.moveCount)}
                {turnSecondsLeft !== null && (
                  <span
                    data-testid="omok-turn-timer"
                    data-urgent={turnSecondsLeft <= 10 ? 'true' : 'false'}
                    aria-label={`남은 시간 ${turnSecondsLeft}초`}
                    style={{
                      marginLeft: 10, padding: '2px 10px', borderRadius: 999, fontFamily: 'monospace', fontSize: 15,
                      background: turnSecondsLeft <= 10 ? '#fee2e2' : '#ecfdf5',
                      color: turnSecondsLeft <= 10 ? '#b91c1c' : '#065f46',
                      border: `1px solid ${turnSecondsLeft <= 10 ? '#fca5a5' : '#a7f3d0'}`,
                      fontWeight: 800,
                    }}
                  >
                    ⏱ {formatTurnClock(turnSecondsLeft)}
                  </span>
                )}
              </div>
            )}
            {isFinished && (
              <div style={resultBannerStyle(room.winner, mySide)} data-testid="omok-result-banner">
                {renderResult(room.winner, mySide, room.winReason, room.winningLine?.length ?? 0)}
              </div>
            )}

            {/* 보드 — side 조회 중 / reveal phase 중엔 interaction 비활성 */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <OmokBoard
                board={room.board}
                lastMove={room.lastMove}
                winningLine={room.winningLine}
                mySide={mySide}
                isMyTurn={isMyTurn}
                disabled={room.status !== 'playing' || !amIParticipant || amIParticipantUnknown || isRevealPhase}
                onPlay={handlePlay}
              />
            </div>

            {isFinished && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button
                  style={{ padding: '10px 20px', background: '#065f46', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                  onClick={handleExit}
                >게임 종료 및 로비로</button>
              </div>
            )}
          </>
        )}
      </div>
      {rulesOpen && <OmokRulesModal onClose={() => setRulesOpen(false)} />}
    </div>
  );
}

// V70G6E — 진영 공개 연출. 서버가 결정한 흑/백 결과를 시각화만 수행.
//   서버가 Firestore 에 영구 기록 → FE 는 결과를 보여줄 뿐. 애니메이션이 결과를 결정하지 않음.
//   reduced-motion 사용자는 애니메이션 축소.
function RevealOverlay({ playerBlack, playerWhite, mySide, reducedMotion }: {
  playerBlack: { displayName: string; orgUnitName: string | null } | null;
  playerWhite: { displayName: string; orgUnitName: string | null } | null;
  mySide: OmokSide | null;
  reducedMotion: boolean;
}) {
  const anim = reducedMotion ? '0ms' : '600ms';
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(2px)',
    }} data-testid="omok-reveal-overlay">
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32,
        maxWidth: 480, width: '90%', textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        animation: `omokReveal ${anim} ease-out`,
      }}>
        <style>{`
          @keyframes omokReveal {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes omokStoneSpin {
            0% { transform: rotateY(0deg); }
            50% { transform: rotateY(540deg); }
            100% { transform: rotateY(720deg); }
          }
        `}</style>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#065f46' }}>
          🎲 진영 결정
        </h2>
        <p style={{ margin: '6px 0 20px', fontSize: 13, color: '#64748b' }}>
          서버가 랜덤으로 흑백을 배정했습니다
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 72, height: 72, margin: '0 auto 10px',
              borderRadius: '50%', background: '#1a1a1a', border: '2px solid #000',
              boxShadow: 'inset -4px -4px 8px rgba(255,255,255,0.1), 0 4px 12px rgba(0,0,0,0.4)',
              animation: reducedMotion ? 'none' : `omokStoneSpin ${anim} ease-in-out`,
            }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
              {playerBlack?.displayName || '-'}
              {mySide === 'black' && ' (나)'}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              흑돌 (선공)
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 72, height: 72, margin: '0 auto 10px',
              borderRadius: '50%', background: '#f5f5f5', border: '2px solid #888',
              boxShadow: 'inset -4px -4px 8px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.3)',
              animation: reducedMotion ? 'none' : `omokStoneSpin ${anim} ease-in-out`,
            }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
              {playerWhite?.displayName || '-'}
              {mySide === 'white' && ' (나)'}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              백돌 (후공)
            </div>
          </div>
        </div>
        <div style={{
          padding: 10, background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 8, fontSize: 13, color: '#065f46', fontWeight: 600,
        }}>
          흑돌 선공 — 곧 대국이 시작됩니다
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ side, player, isCurrentTurn, isMe }: {
  side: 'black' | 'white';
  player: { displayName: string; orgUnitName: string | null } | null;
  isCurrentTurn: boolean;
  isMe: boolean;
}) {
  return (
    <div style={{
      padding: 12,
      background: isCurrentTurn ? '#dcfce7' : '#fff',
      border: isCurrentTurn ? '2px solid #065f46' : '1px solid #e2e8f0',
      borderRadius: 8,
      transition: 'all 160ms',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{
          width: 20, height: 20, borderRadius: '50%',
          background: side === 'black' ? '#1a1a1a' : '#f5f5f5',
          border: side === 'white' ? '1px solid #888' : '1px solid #000',
        }} />
        <strong style={{ fontSize: 13, color: '#0f172a' }}>
          {side === 'black' ? '흑돌 (선공)' : '백돌 (후공)'}
          {isMe && ' · 나'}
        </strong>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
        {player?.displayName || '대기 중…'}
      </div>
      {player?.orgUnitName && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{player.orgUnitName}</div>
      )}
    </div>
  );
}

function renderResult(
  winner: OmokPublicRoom['winner'],
  mySide: OmokSide | null,
  winReason: OmokPublicRoom['winReason'],
  winningLineLength: number,
): React.ReactNode {
  const view = buildResultView(winner, winReason, mySide, winningLineLength);
  const color = view.outcome === 'win' ? '#f59e0b' : view.outcome === 'lose' ? '#64748b' : undefined;
  if (view.outcome === 'unknown') return view.text;
  return <><Trophy size={20} color={color} /> {view.outcome === 'win' ? '🎉 ' : ''}{view.text}</>;
}

function resultBannerStyle(winner: 'black' | 'white' | 'draw' | null, mySide: OmokSide | null): React.CSSProperties {
  const won = mySide !== null && winner === mySide;
  const lost = mySide !== null && winner !== null && winner !== 'draw' && winner !== mySide;
  const bg = won ? '#fef3c7' : lost ? '#fee2e2' : '#dbeafe';
  const fg = won ? '#92400e' : lost ? '#991b1b' : '#1e40af';
  return {
    padding: 16, background: bg, borderRadius: 10,
    textAlign: 'center', fontSize: 18, fontWeight: 700, color: fg, marginBottom: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  };
}

const cardStyle: React.CSSProperties = {
  background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 16,
};
const backBtnStyle: React.CSSProperties = {
  padding: '6px 12px', background: '#f3f4f6', color: '#475569', border: '1px solid #cbd5e1',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4,
};
const dangerBtnStyle: React.CSSProperties = {
  padding: '6px 12px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4,
};
const errBoxStyle: React.CSSProperties = {
  padding: 12, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8,
  color: '#991b1b', marginBottom: 16, fontSize: 13,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

export default OmokRoomPage;
